import React, { useState, useEffect, useCallback } from 'react';
const API = process.env.REACT_APP_API_URL || 'https://tcio-api.onrender.com';

const styles = {
  wrap: { marginTop: 8 },
  bar: { display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' },
  btn: { background: '#336090', color: '#fff', border: 'none', borderRadius: 7, padding: '5px 12px', fontSize: 12.5, fontWeight: 700, cursor: 'pointer' },
  ghost: { background: '#f1f5f9', color: '#334155', border: '1px solid #e2e8f0', borderRadius: 7, padding: '4px 10px', fontSize: 12, fontWeight: 600, cursor: 'pointer' },
  row: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8, padding: '7px 10px', marginTop: 6, flexWrap: 'wrap' },
  badge: { background: '#fef2f2', color: '#b91c1c', border: '1px solid #fecaca', borderRadius: 6, fontSize: 11, fontWeight: 700, padding: '2px 7px' },
  ok: { background: '#f0fdf4', color: '#166534', border: '1px solid #bbf7d0', borderRadius: 6, fontSize: 11, fontWeight: 700, padding: '2px 7px' },
};

export default function VobDocs({ leadId, user, compact }) {
  const headers = { Authorization: `Bearer ${user.token}` };
  const [docs, setDocs] = useState([]);
  const [open, setOpen] = useState(!compact);
  const [busy, setBusy] = useState(false);
  const [showDx, setShowDx] = useState(null);
  const [viewer, setViewer] = useState(null); // { url, filename }

  const view = async (id) => {
    const r = await fetch(`${API}/api/vob-documents/${id}/download`, { headers });
    const j = await r.json();
    if (j.ok) setViewer({ url: j.url, filename: j.filename });
  };

  const load = useCallback(async () => {
    if (!leadId) return;
    try {
      const r = await fetch(`${API}/api/leads/${leadId}/vob-documents`, { headers });
      const j = await r.json();
      if (j.ok) setDocs(j.documents);
    } catch (e) { console.error(e); }
    // eslint-disable-next-line
  }, [leadId]);
  useEffect(() => { load(); }, [load]);

  const uploadFile = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append('vob_pdf', file);
      const r = await fetch(`${API}/api/leads/${leadId}/vob-upload`, { method: 'POST', headers, body: fd });
      const j = await r.json();
      if (!j.ok) alert('Upload failed: ' + (j.error || 'unknown'));
      setTimeout(load, 1200);
      setTimeout(load, 16000); // catch AI extract completing
    } catch (e) { alert('Upload failed: ' + e.message); }
    setBusy(false);
  };

  const download = async (id) => {
    const r = await fetch(`${API}/api/vob-documents/${id}/download`, { headers });
    const j = await r.json();
    if (j.ok) window.open(j.url, '_blank');
  };

  const del = async (id, filename) => {
    if (!window.confirm(`Remove ${filename}? (Recoverable — it is archived, not destroyed.)`)) return;
    await fetch(`${API}/api/vob-documents/${id}`, { method: 'DELETE', headers });
    load();
  };

  const dt = (d) => new Date(d).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });

  if (!leadId) return null;
  return (
    <div style={styles.wrap}>
      <div style={styles.bar}>
        {compact && (
          <button style={styles.ghost} onClick={() => setOpen(o => !o)}>
            📄 VOB Docs ({docs.length})
          </button>
        )}
        {open && (
          <label style={{ ...styles.btn, opacity: busy ? 0.6 : 1 }}>
            {busy ? 'Uploading…' : '+ Upload VOB'}
            <input type="file" accept=".pdf,image/*" style={{ display: 'none' }} disabled={busy}
              onChange={e => { uploadFile(e.target.files[0]); e.target.value = ''; }} />
          </label>
        )}
        {!compact && docs.length === 0 && <span style={{ fontSize: 12.5, color: '#94a3b8' }}>No VOB documents on file.</span>}
      </div>
      {open && docs.map(d => (
        <div key={d.id} style={styles.row}>
          <div style={{ minWidth: 0 }}>
            <span style={{ fontWeight: 700, fontSize: 13 }}>{d.filename}</span>
            <span style={{ color: '#94a3b8', fontSize: 11.5, marginLeft: 8 }}>
              {d.source === 'email' ? 'from email' : 'by ' + (d.uploader_name || '?')} · {dt(d.created_at)}
            </span>
            <div style={{ marginTop: 3, display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              {d.extract_status === 'pending' && <span style={{ ...styles.ok, background: '#eff6ff', color: '#1e40af', border: '1px solid #bfdbfe' }}>AI reading…</span>}
              {d.extract_status === 'done' && !d.discrepancies && <span style={styles.ok}>✓ Matches lead</span>}
              {d.extract_status === 'done' && d.discrepancies && (
                <button style={{ ...styles.badge, cursor: 'pointer' }} onClick={() => setShowDx(showDx === d.id ? null : d.id)}>
                  ⚠ {d.discrepancies.length} mismatch{d.discrepancies.length > 1 ? 'es' : ''} — review
                </button>
              )}
              {['no_text', 'parse_failed'].includes(d.extract_status) && <span style={{ ...styles.badge, background: '#fffbeb', color: '#92400e', border: '1px solid #fde68a' }}>Could not auto-read</span>}
            </div>
            {showDx === d.id && d.discrepancies && (
              <div style={{ marginTop: 6, fontSize: 12, background: '#fff', border: '1px solid #fecaca', borderRadius: 7, padding: '7px 10px' }}>
                {d.discrepancies.map((x, i) => (
                  <div key={i} style={{ marginBottom: 3 }}>
                    <b>{x.field}</b>: document says <b style={{ color: '#166534' }}>{String(x.document)}</b>, lead says <b style={{ color: '#b91c1c' }}>{String(x.lead)}</b>
                  </div>
                ))}
                <div style={{ color: '#64748b', marginTop: 4 }}>The document is usually right — verify, then correct the lead via Edit.</div>
              </div>
            )}
          </div>
          <div style={{ display: 'flex', gap: 6 }}>
            <button style={{ ...styles.ghost, background: '#eff6ff', color: '#1e40af', border: '1px solid #bfdbfe' }} onClick={() => view(d.id)}>View</button>
            <button style={styles.ghost} onClick={() => download(d.id)}>Download</button>
            <button style={{ ...styles.ghost, color: '#b91c1c' }} onClick={() => del(d.id, d.filename)}>Remove</button>
          </div>
        </div>
      ))}
      {viewer && (
        <div onClick={() => setViewer(null)}
          style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.65)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
          <div onClick={e => e.stopPropagation()}
            style={{ background: '#fff', borderRadius: 12, width: 'min(900px, 96vw)', height: 'min(85vh, 1000px)', display: 'flex', flexDirection: 'column', overflow: 'hidden', boxShadow: '0 20px 60px rgba(0,0,0,0.4)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 16px', borderBottom: '1px solid #e2e8f0' }}>
              <span style={{ fontWeight: 700, fontSize: 14, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{viewer.filename}</span>
              <button onClick={() => setViewer(null)} style={{ ...styles.ghost, fontSize: 15, fontWeight: 800 }}>✕</button>
            </div>
            <iframe title="vob-viewer" src={viewer.url} style={{ border: 'none', flex: 1, width: '100%' }} />
          </div>
        </div>
      )}
    </div>
  );
}
